import {
  type AVATAR_SYMBOL_GROUPS,
  AVATAR_SYMBOLS,
  type AvatarSymbolKey,
  type CharacterKey,
  type CharacterTrait,
} from '@secretario/shared';
import { API_BASE } from './api/http.js';
import { riveOn } from './riveFlag.js';

/**
 * Elenco de personagens do Secretário (renders 3D "designer toy", 18 chaves × 6 expressões).
 * Arquivos: /characters/<key>-<expressao>.webp (manifesto em docs/.../ASSET_MANIFEST.md).
 */
export type Gender = 'ela' | 'ele' | 'neutro';
export type Expression = CharacterTrait;

export const EXPRESSIONS: Expression[] = [
  'simpatico',
  'direto',
  'divertido',
  'elegante',
  'tranquilo',
  'espontaneo',
];

export const TRAIT_LABEL: Record<Expression, string> = {
  simpatico: 'Simpático',
  direto: 'Direto',
  divertido: 'Divertido',
  elegante: 'Elegante',
  tranquilo: 'Tranquilo',
  espontaneo: 'Espontâneo',
};

/** Personagem padrão: o de conta nova e o que aparece enquanto o perfil ainda não carregou. */
export const DEFAULT_CHARACTER: CharacterKey = 'traco-ele';

export type CharacterVariant = { key: CharacterKey; name: string };

export type CharacterFamily = {
  id: string;
  label: string;
  desc: string;
  /** cor do "pod" na galeria; combina com o fundo pêssego dos renders */
  pod: string;
  variants: Partial<Record<Gender, CharacterVariant>>;
};

export const FAMILIES: CharacterFamily[] = [
  {
    id: 'pessoa',
    label: 'Pessoa',
    desc: 'O clássico, pra qualquer um.',
    pod: '#FFE0C4',
    variants: {
      ela: { key: 'pessoa-ela', name: 'Nina' },
      ele: { key: 'pessoa-ele', name: 'Théo' },
    },
  },
  {
    id: 'profissional',
    label: 'Profissional',
    desc: 'Empresária ou empresário.',
    pod: '#E7D8FF',
    variants: {
      ela: { key: 'profissional-ela', name: 'Valentina' },
      ele: { key: 'profissional-ele', name: 'Vitor' },
    },
  },
  {
    id: 'outra',
    label: 'Outra pessoa',
    desc: 'Mais tons e estilos.',
    pod: '#FFE0C4',
    variants: {
      ela: { key: 'outra-ela', name: 'Aline' },
      ele: { key: 'outra-ele', name: 'Rui' },
    },
  },
  {
    id: 'jovem',
    label: 'Jovem',
    desc: 'Menina ou garoto.',
    pod: '#FFDCE8',
    variants: {
      ela: { key: 'jovem-ela', name: 'Lila' },
      ele: { key: 'jovem-ele', name: 'Léo' },
    },
  },
  {
    id: 'idoso',
    label: 'Experiente',
    desc: 'Dona Alzira ou Seu Anísio.',
    pod: '#E2EFDF',
    variants: {
      ela: { key: 'idoso-ela', name: 'Dona Alzira' },
      ele: { key: 'idoso-ele', name: 'Seu Anísio' },
    },
  },
  {
    id: 'crianca',
    label: 'Criança',
    desc: 'Bela ou Cauê.',
    pod: '#FFF0C4',
    variants: {
      ela: { key: 'crianca-ela', name: 'Bela' },
      ele: { key: 'crianca-ele', name: 'Cauê' },
    },
  },
  {
    id: 'heroi',
    label: 'Super-herói',
    desc: 'Capa, uniforme e o "S" de Secretário.',
    pod: '#FFD9B3',
    variants: {
      ela: { key: 'heroi-ela', name: 'Nova' },
      ele: { key: 'heroi-ele', name: 'Raio' },
    },
  },
  {
    id: 'robo',
    label: 'Robô',
    desc: 'Tech de verdade.',
    pod: '#D7F0EC',
    variants: { neutro: { key: 'robo', name: 'Robic' } },
  },
  {
    id: 'et',
    label: 'ET estiloso',
    desc: 'De outro planeta.',
    pod: '#E7DBFF',
    variants: { neutro: { key: 'et', name: 'Ziggy' } },
  },
  {
    id: 'cao',
    label: 'Cão tech',
    desc: 'Companheiro fiel.',
    pod: '#FFE6C9',
    variants: { neutro: { key: 'cao', name: 'Bit' } },
  },
  {
    id: 'gato',
    label: 'Gato tech',
    desc: 'Independente e esperto.',
    pod: '#E2DEF0',
    variants: { neutro: { key: 'gato', name: 'Mel' } },
  },
  {
    id: 'robo-s',
    label: 'Robô S',
    desc: 'Fone, o "S" no peito e corpo 3D na conversa ao vivo.',
    pod: '#FFE0CC',
    variants: { neutro: { key: 'robo-s', name: 'Tito' } },
  },
  {
    id: 'gota',
    label: 'Gota',
    desc: 'Macia, expressiva e com corpo 3D na conversa ao vivo.',
    pod: '#E9E1FF',
    variants: { neutro: { key: 'gota', name: 'Lumi' } },
  },
  // A traço: uma família por pessoa, para cada uma ter o próprio lugar na escolha.
  {
    id: 'traco-ela',
    label: 'Clara',
    desc: 'Desenhada a tinta, como numa revista. Acompanha você com o olhar.',
    pod: '#FCF7F1',
    variants: { ela: { key: 'traco-ela', name: 'Clara' } },
  },
  {
    id: 'traco-ele',
    label: 'Caio',
    desc: 'Desenhado a tinta, como numa revista. Acompanha você com o olhar.',
    pod: '#FCF7F1',
    variants: { ele: { key: 'traco-ele', name: 'Caio' } },
  },
  {
    id: 'traco-igor',
    label: 'Igor',
    desc: 'Desenhado a tinta, com expressões de verdade: cada boca e cada olhar desenhados.',
    pod: '#FCF7F1',
    variants: { ele: { key: 'traco-igor', name: 'Igor' } },
  },
  {
    // Helena (15/10): nome que o "Ô, <nome>" do modo mesa entende fácil, três sílabas que o
    // modelo de voz do aparelho conhece e longe de palavras do dia a dia ("Lia" se confundia
    // com "olha" e com a Lila do elenco).
    id: 'traco-ela2',
    label: 'Helena',
    desc: 'Desenhada a tinta, com expressões de verdade: cada boca e cada olhar desenhados.',
    pod: '#FCF7F1',
    variants: { ela: { key: 'traco-ela2', name: 'Helena' } },
  },
  {
    id: 'face-sol',
    label: 'Sol',
    desc: 'Massinha amarela, cheia de expressão.',
    pod: '#F8BC17',
    variants: { neutro: { key: 'face-sol', name: 'Sol' } },
  },
  {
    id: 'face-brasa',
    label: 'Brasa',
    desc: 'Massinha laranja, calorosa.',
    pod: '#FD7E3B',
    variants: { neutro: { key: 'face-brasa', name: 'Brasa' } },
  },
  {
    id: 'face-pessego',
    label: 'Pêssego',
    desc: 'Massinha pêssego, gentil.',
    pod: '#FD9373',
    variants: { neutro: { key: 'face-pessego', name: 'Pêssego' } },
  },
  {
    id: 'face-piscina',
    label: 'Piscina',
    desc: 'Massinha azul, leve e alegre.',
    pod: '#4BC3D8',
    variants: { neutro: { key: 'face-piscina', name: 'Piscina' } },
  },
  {
    id: 'face-lavanda',
    label: 'Lavanda',
    desc: 'Massinha lilás, tranquila.',
    pod: '#A3AAEA',
    variants: { neutro: { key: 'face-lavanda', name: 'Lavanda' } },
  },
];

/** Símbolo 3D de um grupo de estilo (mesma imagem em toda expressão; o movimento é do CSS). */
export function isSymbolAvatar(key: string): key is AvatarSymbolKey {
  return key.startsWith('sym-');
}

/** Avatar feito a partir da foto da pessoa ("Crie o seu"); a imagem vem da API. */
export function isOwnAvatar(key: string): boolean {
  return key.startsWith('own-');
}

/**
 * Revisão da arte de um personagem: o servidor guarda as imagens por 30 dias como imutáveis,
 * então arte refeita com o mesmo nome ganha `?v=` para o aparelho buscar a nova.
 */
const ART_REVISION: Readonly<Record<string, number>> = {
  'traco-ela': 2,
  'traco-ele': 2,
  'traco-igor': 9,
  'traco-ela2': 2,
};
export function artRevision(key: string): string {
  const rev = ART_REVISION[key];
  return rev ? `?v=${rev}` : '';
}

export function characterImage(key: CharacterKey, expression: Expression): string {
  if (isOwnAvatar(key)) return `${API_BASE}/v1/avatars/${key.slice(4)}/${expression}`;
  if (isSymbolAvatar(key)) {
    const [, group, symbol] = key.split('-');
    return `/styles/${group}/${symbol}.webp`;
  }
  // Faces: fora do card do Início e do catálogo, a medalha redonda (rosto no centro, com folga).
  if (key.startsWith('face-')) return `/characters/${key}-${expression}-badge.webp`;
  // Sem os personagens em Rive ligados, a Gota parada é a mesma arte da Gota viva (a anterior).
  if (key === 'gota' && !riveOn()) return `/characters/classica/gota-${expression}.webp`;
  return `/characters/${key}-${expression}.webp${artRevision(key)}`;
}

/** Arte inteira do personagem (para o card do Início e o catálogo): nos Faces, o rosto em quadro cheio. */
export function characterTile(key: CharacterKey, expression: Expression): string {
  if (key.startsWith('face-')) return `/characters/${key}-${expression}.webp`;
  return characterImage(key, expression);
}

/**
 * Grupos de estilo que a escolha do Secretário oferece: Ao vivo 3D (personagens
 * que ganham corpo 3D, com gestos e expressões, na conversa ao vivo) e Faces.
 * O 3D Toy e os símbolos (Vidro, Editorial, Massinha, Holográfico) saíram da
 * escolha; quem já usa continua com eles até escolher outro, e as chaves antigas
 * seguem valendo em FAMILIES, findVariant e characterImage.
 */
export type StyleGroupId = 'live' | 'traco' | 'faces';

export const STYLE_GROUPS: { id: StyleGroupId; label: string; desc: string }[] = [
  {
    id: 'live',
    label: 'Ao vivo 3D',
    desc: 'Na conversa ao vivo, eles gesticulam, mudam de expressão e reagem à sua voz.',
  },
  {
    id: 'traco',
    label: 'A traço',
    desc: 'Ilustração a tinta sobre papel: ela e ele viram a cabeça, olham para o que importa e falam com você.',
  },
  {
    id: 'faces',
    label: 'Faces',
    desc: 'Só o rosto, ocupando a tela: olhos, sobrancelhas e boca que falam com você.',
  },
];

/** Famílias do 3D Toy. Saíram da escolha; quem já usa uma delas continua com ela. */
export const TOY_FAMILY_IDS = [
  'pessoa',
  'profissional',
  'outra',
  'jovem',
  'idoso',
  'crianca',
  'heroi',
  'robo',
  'et',
  'cao',
  'gato',
];

/** Famílias do "Ao vivo 3D". */
export const LIVE_FAMILY_IDS = ['gota', 'robo-s'];

/** Famílias do "A traço". */
export const TRACO_FAMILY_IDS = ['traco-ela', 'traco-ele', 'traco-igor', 'traco-ela2'];

/** Famílias dos "Faces". */
export const FACE_FAMILY_IDS = [
  'face-sol',
  'face-brasa',
  'face-pessego',
  'face-piscina',
  'face-lavanda',
];

/** Cor de fundo da arte de um Face (a pele é o próprio fundo); null para os demais personagens. */
export function faceBackground(key: string): string | null {
  if (!FACE_FAMILY_IDS.includes(key)) return null;
  return FAMILIES.find((family) => family.id === key)?.pod ?? null;
}

export const SYMBOL_INFO: Record<
  (typeof AVATAR_SYMBOLS)[number],
  { name: string; trait: Expression }
> = {
  sol: { name: 'Sol', trait: 'simpatico' },
  flecha: { name: 'Flecha', trait: 'direto' },
  balao: { name: 'Papo', trait: 'divertido' },
  diamante: { name: 'Diamante', trait: 'elegante' },
  lua: { name: 'Lua', trait: 'tranquilo' },
  raio: { name: 'Raio', trait: 'espontaneo' },
};

export function symbolKeysOf(group: (typeof AVATAR_SYMBOL_GROUPS)[number]): AvatarSymbolKey[] {
  return AVATAR_SYMBOLS.map((symbol) => `sym-${group}-${symbol}` as AvatarSymbolKey);
}

export function findVariant(key: CharacterKey): {
  family: CharacterFamily;
  variant: CharacterVariant;
} {
  for (const family of FAMILIES) {
    for (const variant of Object.values(family.variants)) {
      if (variant?.key === key) return { family, variant };
    }
  }
  const family = FAMILIES[0]!;
  return { family, variant: family.variants.ela! };
}

export function characterName(key: CharacterKey): string {
  if (isOwnAvatar(key)) return 'Secretário';
  if (isSymbolAvatar(key)) {
    const symbol = key.split('-')[2] as (typeof AVATAR_SYMBOLS)[number];
    return SYMBOL_INFO[symbol]?.name ?? 'Secretário';
  }
  return findVariant(key).variant.name;
}
